import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import './AgronetUsersReport.scss';
import AgronetProCompanyDetails from './AgronetProCompanyDetails';
import UserSubscriptionModal from './UserSubscriptionModal';

const defaults = {search: '', loginStatus: 'ALL', lastLoginFrom: '', lastLoginTo: '', minSensors: '', maxSensors: ''};
const reportBody = (filters, sort, page, size) => ({page, numberOnPage: size, ...sort, ...filters,
    lastLoginFrom: filters.lastLoginFrom || null, lastLoginTo: filters.lastLoginTo || null,
    minSensors: filters.minSensors === '' ? null : Number(filters.minSensors),
    maxSensors: filters.maxSensors === '' ? null : Number(filters.maxSensors)});
const reportHeaders = () => ({'Content-Type': 'application/json',
    Authorization: sessionStorage.getItem('agronetToken'), Local: localStorage.getItem('lang') || 'en'});

export default function AgronetUsersReport() {
    const t = useSelector(state => state.language.translator);
    const [draft, setDraft] = useState({...defaults});
    const [filters, setFilters] = useState({...defaults});
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(50);
    const [sort, setSort] = useState({sortBy: 'LAST_LOGIN', direction: 'DESC'});
    const [result, setResult] = useState(null);
    const [pending, setPending] = useState(false);
    const [error, setError] = useState('');
    const [refresh, setRefresh] = useState(0);
    const [downloading, setDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState('');
    const [companyUser, setCompanyUser] = useState(null);
    const [subscriptionUser, setSubscriptionUser] = useState(null);
    const text = value => t.gettext(value);

    useEffect(() => {
        const controller = new AbortController();
        setPending(true);
        setError('');
        setResult(null);
        const body = reportBody(filters, sort, page, size);
        fetch(`${process.env.REACT_APP_API_URL}/custom/users/agronet_users_report`, {
            method: 'POST', signal: controller.signal,
            headers: reportHeaders(),
            body: JSON.stringify(body)
        }).then(response => {
            if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
            return response.json();
        }).then(data => {
            if (!controller.signal.aborted) setResult(data);
        }).catch(err => {
            if (!controller.signal.aborted) setError(err.message);
        }).finally(() => {
            if (!controller.signal.aborted) setPending(false);
        });
        return () => controller.abort();
    }, [filters, page, size, sort, refresh]);

    const downloadCsv = async () => {
        setDownloading(true);
        setDownloadError('');
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/custom/users/agronet_users_report/csv`, {
                method: 'POST', headers: reportHeaders(),
                body: JSON.stringify(reportBody(filters, sort, page, size))
            });
            if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'agronet-users-report.csv';
            try {
                document.body.appendChild(link);
                link.click();
            } finally {
                link.remove();
                window.URL.revokeObjectURL(url);
            }
        } catch (err) {
            setDownloadError(err.message);
        } finally {
            setDownloading(false);
        }
    };

    const field = (key, value) => setDraft(previous => ({...previous, [key]: value}));
    const apply = event => {
        event.preventDefault();
        setError('');
        if (draft.lastLoginFrom && draft.lastLoginTo && draft.lastLoginFrom > draft.lastLoginTo) {
            setError(text('Start date must be before end date')); return;
        }
        if (draft.minSensors !== '' && draft.maxSensors !== '' && Number(draft.minSensors) > Number(draft.maxSensors)) {
            setError(text('Minimum sensors must not exceed maximum sensors')); return;
        }
        setPage(1); setFilters({...draft});
    };
    const changeSort = sortBy => {
        setPage(1);
        setSort(previous => ({sortBy, direction: previous.sortBy === sortBy ?
            (previous.direction === 'ASC' ? 'DESC' : 'ASC') : (sortBy === 'NAME' ? 'ASC' : 'DESC')}));
    };
    const heading = (label, sortBy) => <th scope="col" aria-sort={sort.sortBy === sortBy ?
        (sort.direction === 'ASC' ? 'ascending' : 'descending') : 'none'}>
        <button type="button" onClick={() => changeSort(sortBy)}>{text(label)}
            {sort.sortBy === sortBy && <span aria-hidden="true"> {sort.direction === 'ASC' ? '↑' : '↓'}</span>}
        </button>
    </th>;

    return <section className="AgronetUsersReport">
        <h2>{text('Agronet users')}</h2>
        <form className="reportFilters" onSubmit={apply}>
            <label>{text('Name or email')}<input type="search" value={draft.search}
                onChange={event => field('search', event.target.value)} /></label>
            <label>{text('Login status')}<select value={draft.loginStatus} onChange={event => {
                const loginStatus = event.target.value;
                setDraft(previous => ({...previous, loginStatus,
                    ...(loginStatus === 'NEVER' ? {lastLoginFrom: '', lastLoginTo: ''} : {})}));
            }}>
                <option value="ALL">{text('All users')}</option>
                <option value="LOGGED_IN">{text('Has logged in')}</option>
                <option value="NEVER">{text('Never logged in')}</option>
            </select></label>
            <label>{text('Last login from')}<input type="date" value={draft.lastLoginFrom}
                disabled={draft.loginStatus === 'NEVER'} onChange={event => field('lastLoginFrom', event.target.value)} /></label>
            <label>{text('Last login to')}<input type="date" value={draft.lastLoginTo}
                disabled={draft.loginStatus === 'NEVER'} onChange={event => field('lastLoginTo', event.target.value)} /></label>
            <label>{text('Minimum sensors')}<input type="number" min="0" step="1" value={draft.minSensors}
                onChange={event => field('minSensors', event.target.value)} /></label>
            <label>{text('Maximum sensors')}<input type="number" min="0" step="1" value={draft.maxSensors}
                onChange={event => field('maxSensors', event.target.value)} /></label>
            <button type="submit">{text('Apply filters')}</button>
            <button type="button" onClick={() => {setDraft({...defaults}); setFilters({...defaults}); setPage(1);}}>{text('Clear')}</button>
        </form>
        <div className="reportToolbar">
            <span aria-live="polite">{result ? `${text('Users')}: ${result.userCount}` : ''}</span>
            <label>{text('Rows per page')} <select value={size} onChange={event => {setSize(Number(event.target.value)); setPage(1);}}>
                {[25, 50, 100, 200, 300, 400].map(value => <option key={value} value={value}>{value}</option>)}
            </select></label>
            <button type="button" onClick={downloadCsv} disabled={downloading}>
                {text(downloading ? 'Downloading CSV' : 'Download CSV')}
            </button>
        </div>
        {downloadError && <div role="alert">{text('Unable to download users report')}: {downloadError}</div>}
        {pending && <p role="status">{text('Loading')}…</p>}
        {error && <div role="alert">{text('Unable to load users')}: {error} <button type="button" onClick={() => setRefresh(value => value + 1)}>{text('Retry')}</button></div>}
        {!pending && result && <>
            <div className="reportTableWrapper"><table>
                <thead><tr><th scope="col">#</th>{heading('Name', 'NAME')}<th scope="col">{text('Email')}</th><th scope="col">{text('VAT number')}</th>
                    {heading('Last login', 'LAST_LOGIN')}{heading('Sensors', 'SENSOR_NUMBER')}
                    {heading('Latest subscription year', 'LATEST_SUBSCRIPTION_YEAR')}
                    {heading('Agronet Pro data', 'HAS_AGRONET_PRO_DATA')}</tr></thead>
                <tbody>{result.users.map((user, index) => <tr key={user.id}>
                    <td>{(page - 1) * size + index + 1}</td>
                    <td>{user.name}</td><td>{user.email}</td><td>{user.vatNumber || '—'}</td>
                    <td>{user.lastLoginDate ? user.lastLoginDate.replace('T', ' ').slice(0, 19) : text('Never logged in')}</td>
                    <td>{user.sensorNumber}</td>
                    <td><button type="button" className="companyDetailsLink" onClick={() => setSubscriptionUser(user)}>
                        {user.latestSubscriptionYear == null ? text('add') : user.latestSubscriptionYear}
                    </button></td>
                    <td>{user.hasAgronetProData ? <button type="button" className="companyDetailsLink"
                        aria-label={`${text('Agronet Pro company details')}: ${user.name || user.email}`}
                        onClick={() => setCompanyUser(user)}>{text('Yes')}</button> : text('No')}</td>
                </tr>)}</tbody>
            </table></div>
            {result.users.length === 0 && <p>{text('No users match these filters')}</p>}
            <nav className="reportPagination" aria-label={text('Pagination')}>
                <button type="button" disabled={page <= 1} onClick={() => setPage(value => value - 1)}>{text('Previous')}</button>
                <span>{text('Page')} {page} / {Math.max(1, result.pageCount)}</span>
                <button type="button" disabled={page >= result.pageCount} onClick={() => setPage(value => value + 1)}>{text('Next')}</button>
            </nav>
        </>}
        {companyUser && <AgronetProCompanyDetails user={companyUser} onClose={() => setCompanyUser(null)}
            subscriptionRefresh={refresh} onAddSubscription={() => setSubscriptionUser(companyUser)} />}
        {subscriptionUser && <UserSubscriptionModal user={subscriptionUser} onClose={() => setSubscriptionUser(null)}
            onSaved={() => {setSubscriptionUser(null); setRefresh(value => value + 1);}} />}
    </section>;
}
